import React, { useEffect } from 'react';
import { Container, Row, Col, Badge, Card, CardBody } from 'reactstrap';
import { Link } from 'react-router-dom';
import './data-processing.scss'

const DataProcessingPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    // Table of contents
    const sections = [
        { id: 'introduction', title: '1. Introduction' },
        { id: 'definitions', title: '2. Definitions' },
        { id: 'duration', title: '3. Duration of the Agreement' },
        { id: 'nature-purpose', title: '4. Nature and Purpose of Processing' },
        { id: 'obligations-processor', title: '5. Obligations of the Processor' },
        { id: 'obligations-controller', title: '6. Obligations of the Controller' },
        { id: 'subprocessing', title: '7. Subprocessing' },
        { id: 'data-subject-rights', title: '8. Data Subject Rights' },
        { id: 'security-measures', title: '9. Security Measures' },
        { id: 'data-breaches', title: '10. Data Breaches' },
        { id: 'data-transfers', title: '11. Data Transfers' },
        { id: 'deletion-return', title: '12. Deletion and Return of Data' },
        { id: 'audit-rights', title: '13. Audit Rights' },
        { id: 'liability', title: '14. Liability' },
        { id: 'governing-law', title: '15. Governing Law' },
        { id: 'contact', title: '16. Contact Information' }
    ];

    return (
        <div className="dpa-page">
            {/* Hero Section */}
            <section className="dpa-hero-section py-5 pt-7 bg-light">
                <Container>
                    <Row className="text-center">
                        <Col lg={8} className="mx-auto">

                            <h1 className="dpa-hero-title display-4 fw-bold mb-4">
                                Data Processing
                                <span className="dpa-gradient-text d-block">Agreement (DPA)</span>
                            </h1>
                            <p className="dpa-hero-subtitle lead text-muted mb-4">
                                This Data Processing Agreement forms part of the agreement between Reachtix
                                and our customers for the processing of personal data in accordance with
                                applicable data protection laws.
                            </p>

                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Main Content */}
            <section className="dpa-main-section py-5">
                <Container>
                    <Row>
                        {/* Sidebar Navigation */}
                        <Col lg={3} className="mb-5 mb-lg-0">
                            <Card className="dpa-sidebar-card sticky-top" style={{ top: '100px' }}>
                                <CardBody className="p-4">
                                    <h6 className="dpa-sidebar-title fw-bold mb-3">Table of Contents</h6>
                                    <nav className="dpa-sidebar-nav">
                                        <ul className="list-unstyled mb-0">
                                            {sections.map((section) => (
                                                <li key={section.id} className="mb-2">
                                                    <a
                                                        href={`#${section.id}`}
                                                        className="dpa-sidebar-link text-decoration-none d-block py-2 border-bottom border-light"
                                                    >
                                                        {section.title}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </nav>
                                    <div className="dpa-sidebar-footer mt-4 pt-3 border-top">
                                        <small className="text-muted">
                                            Questions? <Link to="/contact" className="dpa-contact-link">Contact our DPO</Link>
                                        </small>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Main Content */}
                        <Col lg={9}>
                            <div className="dpa-content-wrapper">
                                {/* Introduction */}
                                <section id="introduction" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">1. Introduction</h2>
                                    <p className="dpa-section-text">
                                        This Data Processing Agreement ("DPA") reflects the parties' agreement
                                        with respect to the processing of personal data by Reachtix ("Processor")
                                        on behalf of the customer ("Controller") in connection with the provision
                                        of Reachtix's services under the terms of the Master Services Agreement
                                        between the parties.
                                    </p>
                                    <p className="dpa-section-text">
                                        This DPA is supplemental to the Master Services Agreement and forms an
                                        integral part thereof. In the event of any conflict between this DPA and
                                        the Master Services Agreement, this DPA shall prevail with respect to
                                        matters concerning the processing of personal data.
                                    </p>
                                    <div className="dpa-alert-info mt-4">
                                        <strong>📌 Key Point:</strong> This DPA is designed to ensure compliance with
                                        the General Data Protection Regulation (GDPR) and other applicable data
                                        protection laws. Both parties agree to comply with their respective
                                        obligations as set forth herein.
                                    </div>
                                </section>

                                {/* Definitions */}
                                <section id="definitions" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">2. Definitions</h2>
                                    <p className="dpa-section-text">
                                        Unless otherwise defined herein, capitalized terms shall have the meanings
                                        assigned to them in the Master Services Agreement. In addition, the following
                                        terms shall have the meanings set out below:
                                    </p>

                                    <div className="dpa-definitions-list mt-4">
                                        <div className="dpa-definition-item mb-4">
                                            <h5 className="dpa-definition-term">2.1 "Data Protection Laws"</h5>
                                            <p className="dpa-definition-text">
                                                Means all applicable laws and regulations relating to the processing
                                                of personal data and privacy, including the General Data Protection
                                                Regulation (EU) 2016/679 ("GDPR"), the UK GDPR, the Data Protection
                                                Act 2018, and any implementing or successor legislation.
                                            </p>
                                        </div>

                                        <div className="dpa-definition-item mb-4">
                                            <h5 className="dpa-definition-term">2.2 "Personal Data"</h5>
                                            <p className="dpa-definition-text">
                                                Means any information relating to an identified or identifiable natural
                                                person that is processed by the Processor on behalf of the Controller
                                                in connection with the Services.
                                            </p>
                                        </div>

                                        <div className="dpa-definition-item mb-4">
                                            <h5 className="dpa-definition-term">2.3 "Processing"</h5>
                                            <p className="dpa-definition-text">
                                                Means any operation or set of operations which is performed on personal
                                                data, whether or not by automated means, including collection, recording,
                                                organization, structuring, storage, adaptation, retrieval, consultation,
                                                use, disclosure, dissemination, or destruction.
                                            </p>
                                        </div>

                                        <div className="dpa-definition-item mb-4">
                                            <h5 className="dpa-definition-term">2.4 "Data Subject"</h5>
                                            <p className="dpa-definition-text">
                                                Means the identified or identifiable natural person to whom the personal
                                                data relates.
                                            </p>
                                        </div>

                                        <div className="dpa-definition-item mb-4">
                                            <h5 className="dpa-definition-term">2.5 "Subprocessor"</h5>
                                            <p className="dpa-definition-text">
                                                Means any third party engaged by the Processor to process personal data
                                                on behalf of the Controller in connection with the Services.
                                            </p>
                                        </div>
                                    </div>
                                </section>

                                {/* Duration of the Agreement */}
                                <section id="duration" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">3. Duration of the Agreement</h2>
                                    <p className="dpa-section-text">
                                        This DPA shall take effect on the effective date of the Master Services
                                        Agreement and shall remain in full force and effect until:
                                    </p>
                                    <ul className="dpa-section-list">
                                        <li>The termination or expiration of the Master Services Agreement; or</li>
                                        <li>The date on which the Processor ceases to process personal data on behalf
                                            of the Controller, whichever is later.</li>
                                    </ul>
                                    <p className="dpa-section-text mt-3">
                                        The obligations of confidentiality and data protection set forth in this DPA
                                        shall survive the termination of this DPA indefinitely.
                                    </p>
                                </section>

                                {/* Nature and Purpose of Processing */}
                                <section id="nature-purpose" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">4. Nature and Purpose of Processing</h2>

                                    <div className="dpa-purpose-table mb-4">
                                        <table className="dpa-data-table">
                                            <thead>
                                                <tr>
                                                    <th>Aspect</th>
                                                    <th>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td><strong>Nature of Processing</strong></td>
                                                    <td>Collection, storage, retrieval, analysis, transmission, and
                                                        deletion of personal data as necessary to provide the Services.</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Purpose of Processing</strong></td>
                                                    <td>To provide, maintain, and improve Reachtix's digital marketing
                                                        services, including analytics, campaign management, reporting,
                                                        and related support services.</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Categories of Data Subjects</strong></td>
                                                    <td>
                                                        <ul className="mb-0">
                                                            <li>Controller's customers and potential customers</li>
                                                            <li>Controller's employees and contractors</li>
                                                            <li>Website visitors</li>
                                                            <li>Marketing leads and contacts</li>
                                                        </ul>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Categories of Personal Data</strong></td>
                                                    <td>
                                                        <ul className="mb-0">
                                                            <li>Names and contact information</li>
                                                            <li>Professional information</li>
                                                            <li>Usage data and analytics</li>
                                                            <li>Cookie and tracking data</li>
                                                            <li>Marketing preferences</li>
                                                        </ul>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Special Categories of Data</strong></td>
                                                    <td>The parties do not anticipate the processing of special categories
                                                        of personal data (sensitive data) under this DPA. If such processing
                                                        is required, the parties shall agree on additional safeguards in writing.</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </section>

                                {/* Obligations of the Processor */}
                                <section id="obligations-processor" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">5. Obligations of the Processor</h2>
                                    <p className="dpa-section-text">
                                        The Processor shall comply with the following obligations when processing
                                        personal data on behalf of the Controller:
                                    </p>

                                    <div className="dpa-obligations-grid mt-4">
                                        <div className="dpa-obligation-card mb-3">
                                            <div className="dpa-obligation-icon">5.1</div>
                                            <div className="dpa-obligation-content">
                                                <h6 className="dpa-obligation-title">Instructions</h6>
                                                <p className="dpa-obligation-text">
                                                    Process personal data only on documented instructions from the Controller,
                                                    unless required to do so by applicable law.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="dpa-obligation-card mb-3">
                                            <div className="dpa-obligation-icon">5.2</div>
                                            <div className="dpa-obligation-content">
                                                <h6 className="dpa-obligation-title">Confidentiality</h6>
                                                <p className="dpa-obligation-text">
                                                    Ensure that persons authorized to process personal data are committed to
                                                    confidentiality or are under an appropriate statutory obligation.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="dpa-obligation-card mb-3">
                                            <div className="dpa-obligation-icon">5.3</div>
                                            <div className="dpa-obligation-content">
                                                <h6 className="dpa-obligation-title">Security Measures</h6>
                                                <p className="dpa-obligation-text">
                                                    Implement appropriate technical and organizational measures to ensure a
                                                    level of security appropriate to the risk.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="dpa-obligation-card mb-3">
                                            <div className="dpa-obligation-icon">5.4</div>
                                            <div className="dpa-obligation-content">
                                                <h6 className="dpa-obligation-title">Subprocessing</h6>
                                                <p className="dpa-obligation-text">
                                                    Not engage another processor without prior specific or general written
                                                    authorization of the Controller.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="dpa-obligation-card mb-3">
                                            <div className="dpa-obligation-icon">5.5</div>
                                            <div className="dpa-obligation-content">
                                                <h6 className="dpa-obligation-title">Data Subject Rights</h6>
                                                <p className="dpa-obligation-text">
                                                    Assist the Controller in responding to requests from data subjects to
                                                    exercise their rights under Data Protection Laws.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="dpa-obligation-card mb-3">
                                            <div className="dpa-obligation-icon">5.6</div>
                                            <div className="dpa-obligation-content">
                                                <h6 className="dpa-obligation-title">Data Breaches</h6>
                                                <p className="dpa-obligation-text">
                                                    Notify the Controller without undue delay after becoming aware of a
                                                    personal data breach affecting Controller's data.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="dpa-obligation-card mb-3">
                                            <div className="dpa-obligation-icon">5.7</div>
                                            <div className="dpa-obligation-content">
                                                <h6 className="dpa-obligation-title">Assistance</h6>
                                                <p className="dpa-obligation-text">
                                                    Assist the Controller in ensuring compliance with obligations regarding
                                                    security, data breach notifications, and data protection impact assessments.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="dpa-obligation-card mb-3">
                                            <div className="dpa-obligation-icon">5.8</div>
                                            <div className="dpa-obligation-content">
                                                <h6 className="dpa-obligation-title">Deletion/Return</h6>
                                                <p className="dpa-obligation-text">
                                                    At the choice of the Controller, delete or return all personal data after
                                                    the end of the Services and delete existing copies unless storage is
                                                    required by law.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {/* Obligations of the Controller */}
                                <section id="obligations-controller" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">6. Obligations of the Controller</h2>
                                    <p className="dpa-section-text">
                                        The Controller acknowledges and agrees that:
                                    </p>
                                    <ul className="dpa-section-list">
                                        <li>
                                            <strong>Lawful Basis:</strong> The Controller is responsible for ensuring
                                            that it has a lawful basis for the processing of personal data and for
                                            providing adequate notices to data subjects.
                                        </li>
                                        <li>
                                            <strong>Accurate Instructions:</strong> The Controller shall provide accurate
                                            and complete instructions to the Processor and shall ensure that processing
                                            in accordance with such instructions complies with Data Protection Laws.
                                        </li>
                                        <li>
                                            <strong>Data Quality:</strong> The Controller is responsible for the accuracy,
                                            quality, and legality of the personal data provided to the Processor.
                                        </li>
                                        <li>
                                            <strong>Consent:</strong> The Controller shall obtain all necessary consents
                                            and provide all necessary notices required for the Processor to lawfully
                                            process personal data as contemplated by this DPA.
                                        </li>
                                        <li>
                                            <strong>Review and Assessment:</strong> The Controller shall review and assess
                                            the information provided by the Processor regarding security measures and
                                            subprocessors to ensure compliance with its obligations.
                                        </li>
                                    </ul>
                                </section>

                                {/* Subprocessing */}
                                <section id="subprocessing" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">7. Subprocessing</h2>
                                    <p className="dpa-section-text">
                                        The Controller provides general authorization for the Processor to engage
                                        subprocessors in connection with the Services, subject to the following conditions:
                                    </p>

                                    <h5 className="dpa-subsection-title mt-4">7.1 Authorized Subprocessors</h5>
                                    <p className="dpa-section-text">
                                        As of the effective date, the Processor engages the following subprocessors:
                                    </p>

                                    <table className="dpa-subprocessors-table mt-3">
                                        <thead>
                                            <tr>
                                                <th>Subprocessor</th>
                                                <th>Purpose</th>
                                                <th>Location</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td><strong>Amazon Web Services (AWS)</strong></td>
                                                <td>Cloud hosting and infrastructure</td>
                                                <td>EU (Ireland)</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Google Cloud Platform</strong></td>
                                                <td>Data analytics and storage</td>
                                                <td>EU (Belgium)</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Stripe</strong></td>
                                                <td>Payment processing</td>
                                                <td>USA (Privacy Shield)</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Intercom</strong></td>
                                                <td>Customer support and communication</td>
                                                <td>USA (Privacy Shield)</td>
                                            </tr>
                                            <tr>
                                                <td><strong>SendGrid</strong></td>
                                                <td>Email communications</td>
                                                <td>USA (Privacy Shield)</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <h5 className="dpa-subsection-title mt-5">7.2 Notification and Objection</h5>
                                    <p className="dpa-section-text">
                                        The Processor shall notify the Controller of any intended changes concerning
                                        the addition or replacement of subprocessors. The Controller may object to such
                                        changes within 14 days of receipt of notice. If the Controller objects and the
                                        Processor cannot provide a reasonable alternative, either party may terminate
                                        the affected Services.
                                    </p>

                                    <h5 className="dpa-subsection-title mt-4">7.3 Subprocessor Obligations</h5>
                                    <p className="dpa-section-text">
                                        The Processor shall impose the same data protection obligations on any
                                        subprocessor as set out in this DPA, ensuring that subprocessors provide
                                        sufficient guarantees to implement appropriate technical and organizational
                                        measures. The Processor remains fully liable to the Controller for the
                                        performance of its subprocessors' obligations.
                                    </p>
                                </section>

                                {/* Data Subject Rights */}
                                <section id="data-subject-rights" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">8. Data Subject Rights</h2>
                                    <p className="dpa-section-text">
                                        The Processor shall assist the Controller in fulfilling its obligations to
                                        respond to requests by data subjects exercising their rights under Data
                                        Protection Laws:
                                    </p>

                                    <div className="dpa-rights-grid mt-4">
                                        <Row>
                                            <Col md={6} className="mb-3">
                                                <Card className="dpa-right-card h-100">
                                                    <CardBody>
                                                        <h6 className="dpa-right-title">Right of Access</h6>
                                                        <p className="dpa-right-text small">
                                                            Processor shall provide Controller with the ability to access
                                                            and retrieve personal data upon request.
                                                        </p>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <Card className="dpa-right-card h-100">
                                                    <CardBody>
                                                        <h6 className="dpa-right-title">Right to Rectification</h6>
                                                        <p className="dpa-right-text small">
                                                            Processor shall enable correction of inaccurate personal data.
                                                        </p>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <Card className="dpa-right-card h-100">
                                                    <CardBody>
                                                        <h6 className="dpa-right-title">Right to Erasure</h6>
                                                        <p className="dpa-right-text small">
                                                            Processor shall delete personal data upon Controller's instruction.
                                                        </p>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <Card className="dpa-right-card h-100">
                                                    <CardBody>
                                                        <h6 className="dpa-right-title">Right to Restriction</h6>
                                                        <p className="dpa-right-text small">
                                                            Processor shall restrict processing when required by Controller.
                                                        </p>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <Card className="dpa-right-card h-100">
                                                    <CardBody>
                                                        <h6 className="dpa-right-title">Data Portability</h6>
                                                        <p className="dpa-right-text small">
                                                            Processor shall provide personal data in structured, commonly used format.
                                                        </p>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <Card className="dpa-right-card h-100">
                                                    <CardBody>
                                                        <h6 className="dpa-right-title">Right to Object</h6>
                                                        <p className="dpa-right-text small">
                                                            Processor shall facilitate objections to processing where applicable.
                                                        </p>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        </Row>
                                    </div>

                                    <p className="dpa-section-text mt-3">
                                        The Processor shall promptly notify the Controller if it receives a request
                                        directly from a data subject and shall not respond to such request except on
                                        the Controller's instructions.
                                    </p>
                                </section>

                                {/* Security Measures */}
                                <section id="security-measures" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">9. Security Measures</h2>
                                    <p className="dpa-section-text">
                                        The Processor shall implement and maintain appropriate technical and organizational
                                        security measures to protect personal data against accidental or unlawful destruction,
                                        loss, alteration, unauthorized disclosure, or access.
                                    </p>

                                    <div className="dpa-security-measures mt-4">
                                        <h5 className="dpa-subsection-title">Technical Measures:</h5>
                                        <Row className="mt-3">
                                            <Col md={6} className="mb-3">
                                                <div className="dpa-security-item d-flex align-items-start">
                                                    <span className="me-3">🔐</span>
                                                    <div>
                                                        <h6 className="dpa-security-title">Encryption</h6>
                                                        <p className="dpa-security-text small">Data encrypted in transit (TLS 1.3) and at rest (AES-256)</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="dpa-security-item d-flex align-items-start">
                                                    <span className="me-3">🛡️</span>
                                                    <div>
                                                        <h6 className="dpa-security-title">Access Controls</h6>
                                                        <p className="dpa-security-text small">Multi-factor authentication, role-based access control</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="dpa-security-item d-flex align-items-start">
                                                    <span className="me-3">📊</span>
                                                    <div>
                                                        <h6 className="dpa-security-title">Monitoring</h6>
                                                        <p className="dpa-security-text small">24/7 system monitoring, intrusion detection systems</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="dpa-security-item d-flex align-items-start">
                                                    <span className="me-3">💾</span>
                                                    <div>
                                                        <h6 className="dpa-security-title">Backups</h6>
                                                        <p className="dpa-security-text small">Daily encrypted backups, geographic redundancy</p>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        <h5 className="dpa-subsection-title mt-4">Organizational Measures:</h5>
                                        <Row className="mt-3">
                                            <Col md={6} className="mb-3">
                                                <div className="dpa-security-item d-flex align-items-start">
                                                    <span className="me-3">👥</span>
                                                    <div>
                                                        <h6 className="dpa-security-title">Training</h6>
                                                        <p className="dpa-security-text small">Annual data protection training for all employees</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="dpa-security-item d-flex align-items-start">
                                                    <span className="me-3">📝</span>
                                                    <div>
                                                        <h6 className="dpa-security-title">Policies</h6>
                                                        <p className="dpa-security-text small">Data protection policies, incident response plans</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="dpa-security-item d-flex align-items-start">
                                                    <span className="me-3">🔍</span>
                                                    <div>
                                                        <h6 className="dpa-security-title">Audits</h6>
                                                        <p className="dpa-security-text small">Regular security assessments and penetration testing</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="dpa-security-item d-flex align-items-start">
                                                    <span className="me-3">📜</span>
                                                    <div>
                                                        <h6 className="dpa-security-title">Certifications</h6>
                                                        <p className="dpa-security-text small">ISO 27001 certified, SOC 2 Type II compliant</p>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </section>

                                {/* Data Breaches */}
                                <section id="data-breaches" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">10. Data Breaches</h2>
                                    <p className="dpa-section-text">
                                        In the event of a personal data breach affecting Controller's data, the Processor shall:
                                    </p>
                                    <ol className="dpa-section-list">
                                        <li>Notify the Controller without undue delay and within 24 hours of becoming aware of the breach.</li>
                                        <li>Provide the Controller with all necessary information to facilitate the Controller's breach notification obligations.</li>
                                        <li>Investigate the breach and take reasonable steps to mitigate its effects.</li>
                                        <li>Cooperate fully with the Controller in addressing the breach.</li>
                                    </ol>
                                    <p className="dpa-section-text mt-3">
                                        The notification shall include at least:
                                    </p>
                                    <ul className="dpa-section-list">
                                        <li>Description of the nature of the breach</li>
                                        <li>Categories and approximate number of data subjects concerned</li>
                                        <li>Categories and approximate number of personal data records concerned</li>
                                        <li>Likely consequences of the breach</li>
                                        <li>Measures taken or proposed to address the breach</li>
                                    </ul>
                                </section>

                                {/* Data Transfers */}
                                <section id="data-transfers" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">11. Data Transfers</h2>
                                    <p className="dpa-section-text">
                                        The Processor may transfer personal data to countries outside the European Economic
                                        Area (EEA) provided that appropriate safeguards are in place:
                                    </p>

                                    <div className="dpa-transfer-mechanisms mt-4">
                                        <div className="dpa-transfer-item mb-3">
                                            <h6 className="dpa-transfer-title">11.1 Standard Contractual Clauses</h6>
                                            <p className="dpa-transfer-text">
                                                For transfers to countries without an adequacy decision, the Processor shall
                                                implement the EU Standard Contractual Clauses (2021/914) as approved by the
                                                European Commission.
                                            </p>
                                        </div>

                                        <div className="dpa-transfer-item mb-3">
                                            <h6 className="dpa-transfer-title">11.2 Adequacy Decisions</h6>
                                            <p className="dpa-transfer-text">
                                                Where the European Commission has determined that a country ensures an adequate
                                                level of protection, transfers to that country may occur without additional
                                                safeguards.
                                            </p>
                                        </div>

                                        <div className="dpa-transfer-item mb-3">
                                            <h6 className="dpa-transfer-title">11.3 UK Transfers</h6>
                                            <p className="dpa-transfer-text">
                                                For transfers from the UK, the Processor shall implement the UK International
                                                Data Transfer Agreement or UK Addendum to the EU SCCs, as applicable.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="dpa-alert-info mt-4">
                                        <strong>🌍 Current Transfer Mechanisms:</strong> The Processor relies on EU Standard
                                        Contractual Clauses for transfers to the United States and other third countries,
                                        supplemented by additional technical and organizational measures where appropriate.
                                    </div>
                                </section>

                                {/* Deletion and Return of Data */}
                                <section id="deletion-return" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">12. Deletion and Return of Data</h2>
                                    <p className="dpa-section-text">
                                        Upon termination or expiration of the Services, the Processor shall, at the
                                        Controller's option, delete or return all personal data processed on behalf of
                                        the Controller.
                                    </p>

                                    <h5 className="dpa-subsection-title mt-4">12.1 Return of Data</h5>
                                    <p className="dpa-section-text">
                                        If the Controller elects to have personal data returned, the Processor shall
                                        provide the data in a structured, commonly used, machine-readable format within
                                        30 days of the request.
                                    </p>

                                    <h5 className="dpa-subsection-title mt-4">12.2 Deletion of Data</h5>
                                    <p className="dpa-section-text">
                                        If the Controller elects to have personal data deleted, the Processor shall
                                        securely delete the data within 30 days, unless retention is required by
                                        applicable law. Upon deletion, the Processor shall provide written certification
                                        that deletion has been completed.
                                    </p>

                                    <h5 className="dpa-subsection-title mt-4">12.3 Retention for Legal Purposes</h5>
                                    <p className="dpa-section-text">
                                        The Processor may retain personal data to the extent required by applicable law,
                                        provided that such data remains subject to the confidentiality and security
                                        obligations of this DPA.
                                    </p>
                                </section>

                                {/* Audit Rights */}
                                <section id="audit-rights" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">13. Audit Rights</h2>
                                    <p className="dpa-section-text">
                                        The Controller has the right to audit the Processor's compliance with this DPA:
                                    </p>

                                    <ul className="dpa-section-list">
                                        <li>
                                            <strong>Frequency:</strong> Audits may be conducted no more than once per
                                            calendar year, unless required by a regulatory authority or in response to
                                            a data breach.
                                        </li>
                                        <li>
                                            <strong>Notice:</strong> The Controller shall provide at least 30 days' prior
                                            written notice of any audit.
                                        </li>
                                        <li>
                                            <strong>Scope:</strong> Audits shall be limited to information necessary to
                                            confirm compliance with this DPA.
                                        </li>
                                        <li>
                                            <strong>Costs:</strong> The Controller shall bear the costs of the audit,
                                            unless the audit reveals material non-compliance, in which case the Processor
                                            shall bear reasonable costs.
                                        </li>
                                        <li>
                                            <strong>Alternative:</strong> Instead of an on-site audit, the Processor may
                                            provide a third-party audit report (e.g., SOC 2) or certification to
                                            demonstrate compliance.
                                        </li>
                                    </ul>
                                </section>

                                {/* Liability */}
                                <section id="liability" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">14. Liability</h2>
                                    <p className="dpa-section-text">
                                        Each party's liability arising out of or in connection with this DPA shall be
                                        subject to the limitations and exclusions of liability set out in the Master
                                        Services Agreement.
                                    </p>
                                    <p className="dpa-section-text">
                                        Notwithstanding the foregoing, nothing in this DPA limits or excludes either
                                        party's liability for:
                                    </p>
                                    <ul className="dpa-section-list">
                                        <li>Fraud or fraudulent misrepresentation</li>
                                        <li>Death or personal injury caused by negligence</li>
                                        <li>Any liability that cannot be limited or excluded under applicable law</li>
                                        <li>Fines and penalties imposed by supervisory authorities due to non-compliance</li>
                                    </ul>
                                </section>

                                {/* Governing Law */}
                                <section id="governing-law" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">15. Governing Law</h2>
                                    <p className="dpa-section-text">
                                        This DPA shall be governed by and construed in accordance with the laws of
                                        England and Wales, without regard to its conflict of laws principles.
                                    </p>
                                    <p className="dpa-section-text">
                                        Any dispute arising out of or in connection with this DPA shall be subject to
                                        the exclusive jurisdiction of the courts of England and Wales, provided that
                                        either party may seek injunctive or other equitable relief in any court of
                                        competent jurisdiction to protect its intellectual property or confidential
                                        information.
                                    </p>
                                </section>

                                {/* Contact Information */}
                                <section id="contact" className="dpa-content-section mb-5">
                                    <h2 className="dpa-section-title fw-bold mb-4">16. Contact Information</h2>
                                    <p className="dpa-section-text">
                                        For any questions or concerns regarding this DPA or data protection matters,
                                        please contact:
                                    </p>

                                    <div className="dpa-contact-details mt-4">
                                        <h5 className="dpa-subsection-title">Reachtix Data Protection Officer</h5>
                                        <ul className="dpa-contact-list list-unstyled">
                                            <li><strong>Email:</strong> <a href="mailto:info@reachtix.com" className="dpa-email-link">info@reachtix.com</a></li>
                                            <li><strong>Phone:</strong> +44 (0)20 1234 5678</li>
                                            <li><strong>Address:</strong> 330 High Holborn, London, WC1V 7QH, United Kingdom</li>
                                        </ul>
                                    </div>

                                    <div className="dpa-contact-details mt-4">
                                        <h5 className="dpa-subsection-title">For Controller Notifications:</h5>
                                        <ul className="dpa-contact-list list-unstyled">
                                            <li><strong>Security Incidents:</strong> <a href="mailto:info@reachtix.com" className="dpa-email-link">info@reachtix.com</a></li>
                                        </ul>
                                    </div>
                                </section>

                                {/* Company Information */}
                                <div className="dpa-company-card card bg-light border-0 mt-5">
                                    <div className="card-body">
                                        <h6 className="dpa-company-title fw-bold">Company Information</h6>
                                        <p className="dpa-company-text mb-2 small">
                                            <strong>Legal Name:</strong> QUANTUMCREST SOLUTIONS LTD (trading as Reachtix)
                                        </p>
                                        <p className="dpa-company-text mb-2 small">
                                            <strong>Company Number:</strong> 16088299
                                        </p>
                                        <p className="dpa-company-text mb-2 small">
                                            <strong>VAT Number:</strong> GB123456789
                                        </p>
                                        <p className="dpa-company-text mb-2 small">
                                            <strong>ICO Registration:</strong> ZA123456
                                        </p>
                                        <p className="dpa-company-text mb-0 small">
                                            <strong>Registered Office:</strong> 330 High Holborn, London, WC1V 7QH, United Kingdom
                                        </p>
                                    </div>
                                </div>

                                {/* Agreement Acceptance */}
                                <div className="dpa-acceptance-section mt-5 p-4 text-center">
                                    <h5 className="dpa-acceptance-title">Acceptance of Data Processing Agreement</h5>
                                    <p className="dpa-acceptance-text">
                                        By using Reachtix's services, you acknowledge that you have read and understood
                                        this Data Processing Agreement and agree to be bound by its terms.
                                    </p>
                                    <div className="dpa-acceptance-buttons mt-4">
                                        <Link to="/contact" className="dpa-acceptance-btn me-3">
                                            Contact DPO
                                        </Link>

                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Quick Links Section */}
            <section className="dpa-quicklinks-section py-5 bg-light">
                <Container>
                    <Row className="text-center">
                        <Col>
                            <h3 className="dpa-quicklinks-title fw-bold mb-4">Related Documents</h3>
                            <div className="dpa-quicklinks-container d-flex justify-content-center flex-wrap gap-3">
                                <Link to="/privacy-policy" className="dpa-quicklink-btn btn btn-outline-primary">
                                    Privacy Policy
                                </Link>
                                <Link to="/terms" className="dpa-quicklink-btn btn btn-outline-primary">
                                    Terms of Service
                                </Link>
                                <Link to="/cookie-policy" className="dpa-quicklink-btn btn btn-outline-primary">
                                    Cookie Policy
                                </Link>

                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default DataProcessingPage;